<template>
  <b-modal
    id="create-award-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    title="Create an award"
    body-class="p-0"
    @hidden="resetState"
  >
    <div v-if="startMode === null" class="px-5 py-4 award-modal-content">
      <h5 class="mb-4"><strong>How do you want to start?</strong></h5>

      <b-row>
        <b-col cols="12" md="6" class="mb-3 mb-md-0">
          <div class="award-card" role="button" @click="chooseStart('new')">
            <div class="icon"><font-awesome-icon :icon="['fad', 'gift']" /></div>
            <div class="title">Create new award</div>
            <div class="description">
              Start from scratch — choose Company, Peer-to-peer or Milestone and configure everything yourself.
            </div>
          </div>
        </b-col>
        <b-col cols="12" md="6">
          <div class="award-card" role="button" @click="chooseStart('duplicate')">
            <div class="icon"><font-awesome-icon :icon="['fad', 'sitemap']" /></div>
            <div class="title">Duplicate an existing award</div>
            <div class="description">
              Use one of your awards as a template. Make one copy or many — ideal for rolling out the same award to
              several divisions or teams.
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <div v-else-if="step === STEP.MODE" class="px-5 py-4 award-modal-content">
      <h5 class="mb-4"><strong>How should this award be given?</strong></h5>

      <b-row>
        <b-col cols="12" md="6" class="mb-3 mb-md-0">
          <div
            class="award-card"
            :class="{ selected: selectedMode === RECOGNITION_PROGRAM_AWARD_MODES.INSTANT }"
            role="button"
            @click="selectMode(RECOGNITION_PROGRAM_AWARD_MODES.INSTANT)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fas', 'bolt']" />
            </div>
            <div class="title">Instant award</div>
            <div class="description">
              Managers give the award directly from the app. There is no nomination or approval step.
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="6">
          <div
            class="award-card"
            :class="{ selected: selectedMode === RECOGNITION_PROGRAM_AWARD_MODES.NOMINATE }"
            role="button"
            @click="selectMode(RECOGNITION_PROGRAM_AWARD_MODES.NOMINATE)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'users']" />
            </div>
            <div class="title">Nominated award</div>
            <div class="description">
              Employees nominate a colleague. Approvers review each nomination and decide the reward.
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <div v-else class="px-5 py-4 award-modal-content">
      <h5 class="mb-4"><strong>What type of award do you want to create?</strong></h5>

      <b-row>
        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.PROGRAM }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.PROGRAM)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'gift']" />
            </div>
            <div class="title">Company</div>
            <div class="description">Managers give it directly, or colleagues nominate and an approver signs off.</div>
            <span class="reward-pill reward-pill--optional">Monetary reward optional</span>
          </div>
        </b-col>

        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.OPEN }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.OPEN)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'party-horn']" />
            </div>
            <div class="title">Peer-to-peer</div>
            <div class="description">Anyone can thank a colleague, any time, for everyday contributions.</div>
            <span class="reward-pill">No monetary reward</span>
          </div>
        </b-col>

        <b-col cols="12" md="4">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.MILESTONE }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.MILESTONE)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'award']" />
            </div>
            <div class="title">Milestone</div>
            <div class="description">Sent automatically on your people's birthdays or service anniversaries.</div>
            <span class="reward-pill reward-pill--optional">Monetary reward optional</span>
          </div>
        </b-col>
      </b-row>
    </div>

    <div v-if="startMode === 'new' && step === STEP.MODE" class="px-4 py-3 footer-container">
      <b-button variant="outline-secondary" @click="goBackToTypeStep">Back</b-button>
      <div class="d-flex flex-nowrap">
        <b-button variant="primary" :disabled="!selectedMode" @click="onSubmit">Create award</b-button>
      </div>
    </div>

    <div v-else-if="startMode === 'new'" class="px-4 py-3 footer-container">
      <small class="text-muted">Not sure? Peer-to-peer is the quickest to set up. You can add the others later.</small>
      <div class="d-flex flex-nowrap">
        <b-button v-if="isProgramSelected" variant="primary" @click="goToModeStep">Continue</b-button>
        <b-button v-else variant="primary" :disabled="!selectedType" @click="onSubmit">Create award</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { RECOGNITION_AWARD_TYPES, RECOGNITION_PROGRAM_AWARD_MODES } from '@/pages/recognition/constants';

const STEP = {
  TYPE: 'type',
  MODE: 'mode',
};

const selectedType = ref(null);
const selectedMode = ref(null);
const startMode = ref(null);
const step = ref(STEP.TYPE);
const modalRef = ref(null);
const $router = useRouter();

const isProgramSelected = computed(() => selectedType.value === RECOGNITION_AWARD_TYPES.PROGRAM);

function select(type) {
  selectedType.value = type;
}

function selectMode(mode) {
  selectedMode.value = mode;
}

function goToModeStep() {
  step.value = STEP.MODE;
}

function goBackToTypeStep() {
  step.value = STEP.TYPE;
  selectedMode.value = null;
}

function chooseStart(mode) {
  if (mode === 'duplicate') {
    if (modalRef.value) {
      modalRef.value.hide();
    }
    $router.push({ name: 'recognition-duplicate-source' });
    return;
  }
  startMode.value = 'new';
}

function resetState() {
  startMode.value = null;
  step.value = STEP.TYPE;
  selectedType.value = null;
  selectedMode.value = null;
}

function onSubmit() {
  if (selectedType.value === RECOGNITION_AWARD_TYPES.PROGRAM) {
    $router.push({ name: 'recognition-create-program', query: { mode: selectedMode.value } });
  }
  if (selectedType.value === RECOGNITION_AWARD_TYPES.OPEN) {
    $router.push({ name: 'recognition-create-open' });
  }
  if (selectedType.value === RECOGNITION_AWARD_TYPES.MILESTONE) {
    $router.push({ name: 'recognition-create-milestone' });
  }

  if (modalRef.value) {
    modalRef.value.hide();
  }
}
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.award-modal-content {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.award-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.award-card:hover {
  border-color: var(--palette-color-core-1-lighten-30);
  box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
}

.award-card.selected {
  border-color: var(--palette-color-core-1-lighten-30);
  box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
}

.select-indicator {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 2px solid var(--palette-color-default-lighten-90);
  background: var(--palette-color-base-white);
}

.award-card.selected .select-indicator {
  border-color: var(--palette-color-brand-primary);
  background: var(--palette-color-brand-primary);
  box-shadow: inset 0 0 0 4px var(--palette-color-base-white);
}

.icon {
  color: var(--palette-color-brand-primary);
  font-size: 2rem;
  margin-bottom: 0.75rem;
}

.title {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: 0.5rem;
}

.description {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 0.75rem;
}

.reward-pill {
  margin-top: auto;
  align-self: flex-start;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25rem;
  background: var(--palette-color-default-lighten-90);
  color: var(--palette-color-default-lighten-20);
}

.reward-pill--optional {
  background: var(--palette-color-core-1-lighten-50);
  color: var(--palette-color-brand-primary);
}

.footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 3rem;
}

/* Keep button labels on a single line and prevent flex shrink */
.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
